import React, { useEffect, useRef, useState } from "react";
import { api, formatError } from "@/lib/api";
import { MessageSquarePlus, X, Bug, Lightbulb, HelpCircle, MoreHorizontal, Send, CheckCircle2 } from "lucide-react";
import { toast } from "@/lib/toast";

export const FEEDBACK_EVENT = "os:open-feedback";
// Optionally pre-tag/prefill: openFeedback({ category: "Other", prefill: "[Verification] " })
export const openFeedback = (opts) => window.dispatchEvent(new CustomEvent(FEEDBACK_EVENT, { detail: opts || {} }));

const CATEGORIES = [
  { value: "Bug", label: "Bug", icon: Bug },
  { value: "Idea", label: "Idea", icon: Lightbulb },
  { value: "Confusing", label: "Confusing", icon: HelpCircle },
  { value: "Other", label: "Other", icon: MoreHorizontal },
];

export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("Bug");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const pending = useRef(null);

  useEffect(() => {
    const handler = (e) => { pending.current = e.detail || null; setOpen(true); };
    window.addEventListener(FEEDBACK_EVENT, handler);
    return () => window.removeEventListener(FEEDBACK_EVENT, handler);
  }, []);

  useEffect(() => {
    if (open) {
      setSent(false);
      setMessage(pending.current?.prefill || "");
      setCategory(pending.current?.category || "Bug");
      pending.current = null;
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 10) { toast.error("Please add a bit more detail — at least 10 characters."); return; }
    setBusy(true);
    try {
      await api.post("/beta/feedback", {
        category,
        message: message.trim(),
        url: window.location.href,
        user_agent: navigator.userAgent,
      });
      setSent(true);
      setTimeout(() => setOpen(false), 1400);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="feedback-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full sm:max-w-md bg-surface border border-sniper animate-fade-up">
            <div className="flex items-center justify-between px-5 py-4 border-b border-sniper">
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight flex items-center gap-2 text-foreground">
                <MessageSquarePlus className="w-5 h-5 text-volt" /> Beta Feedback
              </h2>
              <button onClick={() => setOpen(false)} data-testid="feedback-close" className="text-secondary-foreground hover:text-foreground transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {sent ? (
              <div className="px-5 py-10 text-center" data-testid="feedback-sent">
                <CheckCircle2 className="w-10 h-10 text-olive-bright mx-auto" />
                <p className="mt-3 font-heading text-lg text-foreground">Thanks — feedback logged.</p>
                <p className="text-xs font-mono text-muted-foreground mt-1">The team reviews every note.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="px-5 py-4 space-y-4">
                <div>
                  <p className="font-mono text-xs uppercase tracking-wide text-secondary-foreground mb-2">Type</p>
                  <div className="grid grid-cols-4 gap-2">
                    {CATEGORIES.map((c) => {
                      const Icon = c.icon;
                      const active = category === c.value;
                      return (
                        <button
                          type="button"
                          key={c.value}
                          data-testid={`feedback-cat-${c.value.toLowerCase()}`}
                          onClick={() => setCategory(c.value)}
                          className={`flex flex-col items-center gap-1 py-2.5 border text-[11px] font-mono uppercase transition-colors ${active ? "border-olive bg-olive/15 text-olive-bright" : "border-sniper text-secondary-foreground hover:text-foreground"}`}
                        >
                          <Icon className="w-4 h-4" /> {c.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label htmlFor="fb-msg" className="font-mono text-xs uppercase tracking-wide text-secondary-foreground">What's on your mind?</label>
                  <textarea
                    id="fb-msg"
                    data-testid="feedback-message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={5}
                    autoFocus
                    placeholder="What worked, what broke, what you'd want next..."
                    className="mt-1 w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground text-sm transition-colors resize-none"
                  />
                  <p className="mt-1.5 text-[11px] font-mono text-muted-foreground truncate">Auto-attached: {window.location.pathname}</p>
                </div>
                <button
                  data-testid="feedback-submit"
                  disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase text-base py-3 transition-colors"
                >
                  {busy ? "Sending..." : <>Send Feedback <Send className="w-4 h-4" /></>}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
